import React from 'react';

import { connect } from 'react-redux';
import { Card } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { PageHeader, AppButton } from '../../components';
import useComponentState from '../../utils/useComponentState';
import { isEmptyResponseError } from '../../utils/apiHelpers';
import { usePermissions } from '../../hooks/usePermissions';
import { createRole } from '../../api/RolePermission';
import { ROLE_MANAGEMENT_PERMISSIONS } from '../../constants/permissions';
import {
  ROLE_PREFIX,
  MAX_SUFFIX_LENGTH,
  INITIAL_STATE,
  formatSuffix,
  validateSuffix,
} from './CreateRoleConstants';

import styles from './CreateRole.module.scss';

const CreateRole = ({ auth }) => {
  const [state, setState] = useComponentState(INITIAL_STATE);
  const token = auth.auth.token;
  const { can } = usePermissions();

  const suffix = state.roleSuffix.trim();
  const suffixError = validateSuffix(suffix);
  const showError = suffix.length > 0 ? suffixError : '';

  const descriptionError = state.description.trim()
    ? ''
    : 'Description is required.';
  const showDescriptionError = state.descriptionTouched ? descriptionError : '';

  const isValid = !suffixError && !descriptionError;

  const handleReset = () =>
    setState({ roleSuffix: '', description: '', descriptionTouched: false });

  const handleCreate = async () => {
    if (!can(ROLE_MANAGEMENT_PERMISSIONS.CREATE_ROLE.VIEW)) {
      toast.error('You are not authorized to perform this action.');
      return;
    }
    if (suffixError || descriptionError) {
      setState({ descriptionTouched: true });
      return;
    }

    const roleName = `${ROLE_PREFIX}${suffix}`;

    setState({ creating: true });
    try {
      await createRole(token, roleName, state.description.trim());
      toast.success('Role created successfully.');
      setState(INITIAL_STATE);
    } catch (err) {
      if (isEmptyResponseError(err)) {
        toast.success('Role created successfully.');
        setState(INITIAL_STATE);
        return;
      }
      setState({ creating: false });
      toast.error(err?.message || 'Failed to create role.');
    }
  };

  return (
    <div className={styles.root_div}>
      <PageHeader text='Create Role' />

      <Card className={styles.formCard}>
        <Card.Body>
          <div className={styles.sectionTitle}>Role Details</div>
          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Role Name *</label>
              <div
                className={`${styles.prefixInput} ${
                  showError ? styles.input_error : ''
                }`}
              >
                <span className={styles.prefix}>{ROLE_PREFIX}</span>
                <input
                  type='text'
                  className={styles.suffixInput}
                  placeholder='CUSTOM_ADMIN'
                  value={state.roleSuffix}
                  maxLength={MAX_SUFFIX_LENGTH}
                  onChange={(e) =>
                    setState({ roleSuffix: formatSuffix(e.target.value) })
                  }
                />
              </div>
              {showError ? (
                <span className={styles.error_message}>{showError}</span>
              ) : (
                <span className={styles.field_hint}>
                  Enter the part after ROLE_ (UPPERCASE letters, numbers and
                  underscores).
                </span>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Description *</label>
              <input
                type='text'
                className={`${styles.input} ${
                  showDescriptionError ? styles.input_error : ''
                }`}
                placeholder='e.g. Customer Care'
                value={state.description}
                onChange={(e) => setState({ description: e.target.value })}
                onBlur={() => setState({ descriptionTouched: true })}
              />
              {showDescriptionError && (
                <span className={styles.error_message}>
                  {showDescriptionError}
                </span>
              )}
            </div>
          </div>

          <div className={styles.formActions}>
            <button
              type='button'
              className={styles.secondaryBtn}
              onClick={handleReset}
              disabled={state.creating}
            >
              Reset
            </button>
            <AppButton
              text={state.creating ? 'Creating...' : 'Create Role'}
              onClick={handleCreate}
              loading={state.creating || !isValid}
              containerStyle={{
                padding: 0,
                minWidth: 150,
                opacity: state.creating || !isValid ? 0.5 : 1,
              }}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(CreateRole);
